import { IAPIRoutes } from "../../../../config";

export interface IFeatureTourStepApi {
  step_id: string;
  target_id: string;
  title: string;
  description: string;
  placement?: string | null;
  sort_order: number;
}

export interface IFeatureTourApi {
  feature_name: string;
  title: string;
  app_scope: string;
  route_pattern?: string | null;
  client_surface?: string | null;
  prerequisite_feature_names: string[];
  required_client_capabilities: string[];
  minimum_minutes_since_first_login?: number | null;
  minimum_minutes_since_last_tour_completion?: number | null;
  completed_steps: string[];
  steps: IFeatureTourStepApi[];
}

export interface IFeatureTourStatusApiResponse {
  first_login_at?: string | null;
  last_tour_completion_at?: string | null;
  completed_features: string[];
  completed_steps_by_feature: Record<string, string[]>;
  eligible_features: IFeatureTourApi[];
}

export interface ICompleteFeatureTourStepApiResponse {
  feature_name: string;
  step_id: string;
  completed_via: string;
  completed_at: string;
  already_completed: boolean;
}

export interface IGetFeatureToursParams {
  appScope?: string;
  clientSurface?: string;
  route?: string;
  clientCapabilities?: string[];
  forceFeatureName?: string | null;
}

export interface ICompleteFeatureTourStepPayload {
  featureName: string;
  stepId: string;
  completedVia: string;
}

export type IFeatureTourRepository = ReturnType<
  typeof FeatureTourRepositoryFactory
>;

export const FeatureTourRepositoryFactory = ({
  APIProvider,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST } = APIProvider;

  const buildQuery = (params: IGetFeatureToursParams) => {
    const query: Record<string, string> = {
      app_scope: params.appScope ?? "cip-app",
    };
    if (params.clientSurface) query.client_surface = params.clientSurface;
    if (params.route) query.route = params.route;
    if (params.clientCapabilities?.length) {
      query.client_capabilities = params.clientCapabilities.join(",");
    }
    if (params.forceFeatureName) {
      query.force_feature_name = params.forceFeatureName;
    }
    return query;
  };

  const getFeatureTours = async (
    params: IGetFeatureToursParams
  ): Promise<IFeatureTourStatusApiResponse> => {
    return await GET(APIRoutes.apiGetFeatureTours, buildQuery(params));
  };

  const getFeatureTourStatus = async (
    params: IGetFeatureToursParams
  ): Promise<IFeatureTourStatusApiResponse> => {
    return await GET(APIRoutes.apiGetFeatureTourStatus, buildQuery(params));
  };

  const completeFeatureTourStep = async (
    payload: ICompleteFeatureTourStepPayload
  ): Promise<ICompleteFeatureTourStepApiResponse> => {
    return await POST(APIRoutes.apiCompleteFeatureTourStep, {
      feature_name: payload.featureName,
      step_id: payload.stepId,
      completed_via: payload.completedVia,
    });
  };

  return {
    getFeatureTours,
    getFeatureTourStatus,
    completeFeatureTourStep,
  };
};
